<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { STEPS } from '../../store/onboarding'
import LogoComponent from '@webapp/components/AppComponents/LogoComponent/LogoComponent.vue'
import AppFooter from '../AppFooter.vue'
import LegalModal from '../LegalModal.vue'

const { t } = useI18n()
const store = useStore()
const route = useRoute()

const legalType = ref(null)

const visibleSteps = STEPS.filter(s => s.label !== null)
const totalVisible = visibleSteps.length

const currentStepKey = computed(() => route.meta?.stepKey)
const currentStepIndex = computed(() => {
    const idx = visibleSteps.findIndex(s => s.key === currentStepKey.value)
    return idx >= 0 ? idx : 0
})
const progressPercent = computed(() => {
    return Math.round(((currentStepIndex.value + 1) / totalVisible) * 100)
})
const currentStepInfo = computed(() => {
    return visibleSteps[currentStepIndex.value]
})
</script>

<template>
    <div class="mp-app">
        <div class="mp-onboarding-container">
            <div class="mp-onboarding-card">
                <div class="mp-logo-wrapper mb-3">
                    <LogoComponent class="img-fluid" />
                    <h1>{{ t('onboarding.appName') }}</h1>
                    <h3>{{ t('onboarding.tagline') }}</h3>
                </div>

                <div class="mp-progress-wrapper mb-3">
                    <div class="d-flex justify-content-between mb-1">
                        <small class="text-muted">
                            {{ t('onboarding.step') }} {{ currentStepIndex + 1 }} / {{ totalVisible }}
                        </small>
                        <small class="text-muted">{{ progressPercent }}%</small>
                    </div>
                    <div class="progress" style="height: 6px;">
                        <div
                            class="progress-bar"
                            role="progressbar"
                            :style="{ width: progressPercent + '%' }"
                        ></div>
                    </div>
                </div>

                <h5 v-if="currentStepInfo?.label" class="mb-3 text-center">
                    {{ t(currentStepInfo.label) }}
                    <span v-if="currentStepInfo.required" class="text-danger">*</span>
                </h5>

                <router-view />
            </div>
        </div>
        <AppFooter @open-legal="legalType = $event" />
    </div>
    <LegalModal :type="legalType" @close="legalType = null" />
</template>

<style lang="scss" scoped>
@import '../../assets/styles.scss';

.mp-onboarding-container {
    min-height: 100vh;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 2rem 1rem;
}
.mp-onboarding-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    padding: 2rem;
    width: 100%;
    max-width: 520px;
}
.mp-logo-wrapper {
    text-align: center;
    img { max-width: 48px; }
}
.mp-progress-wrapper {
    .progress-bar {
        background-color: $mp-primary;
        transition: width 0.3s ease;
    }
}
</style>
